import { useEffect } from "react";
import create from "zustand";
import { Resource } from "../types/Response";
import { Options, useInfiniteCollection } from "./useInfiniteCollection";

export const useAutoFetchCollection = <T extends Resource>(
    path: string | string[],
    queryParams?: any,
    options?: Options<T>,
) => {
    const response = useInfiniteCollection<T>(path, queryParams, options);
    const fetchLockKey = Array.isArray(path) ? path.join("-") : path;

    const { setLock, isLocked } = useFetchLockStore();

    // Whenever there is paginated data and we are calling this hook from multiple places, We try to simultaneously fetch the next page.
    // Added lock to make sure that we dont fetch the next page again if there is already a fetch going on.
    useEffect(() => {
        if (!isLocked(fetchLockKey) && response.hasNextPage && !response.isFetchingNextPage && !response.isFetching) {
            setLock(fetchLockKey, true);
            response.fetchNextPage().finally(() => {
                setLock(fetchLockKey, false);
            });
        }
    }, [response, fetchLockKey, isLocked, setLock]);

    return response;
};

interface FetchLockState {
    locks: Record<string, boolean>;
    setLock: (key: string, value: boolean) => void;
    isLocked: (key: string) => boolean;
}

const useFetchLockStore = create<FetchLockState>((set, get) => ({
    locks: {},
    setLock: (key, value) => set((state) => ({ locks: { ...state.locks, [key]: value } })),
    isLocked: (key) => !!get().locks[key],
}));
